import { Injectable, Injector, NgZone } from '@angular/core';
import { GoogleTagManagerService } from '@app/core/lib/google-tag-manager/angular-google-tag-manager.service';
import { LocalStorageService } from '@app/core/services/local-storage.service';
import { LoggerService } from '@app/core/services/logger.service';
import { RawFileStoreService } from '@app/core/services/raw-file-store.service';
import * as fromRoot from '@app/reducers';
import { buildCloudfrontError } from '@app/shared/helpers/cloudfront';
import { buildUploadInput, parseUploaderError } from '@app/shared/helpers/uploader';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { CustomEventEmitter, FinishedEvent, QueuedEvent, SmashUploader, StartedEvent, StatusEvent, UploadCanceledOutput, UploadCompleteOutput, UploaderError, UploaderEvents, UploaderParameters, UploaderStatus, UploadProgressEvent } from '@smash-sdk/uploader';
import { defer, forkJoin, of } from 'rxjs';
import { catchError, concatMap, map, take } from 'rxjs/operators';
import { Constant } from 'src/constant';
import * as fromUploader from '@app/client/uploader/store/reducers';
import { EmailDelivery, LinkDelivery, TransferCommonParameters } from '@app/client/uploader/interfaces/transfer';
import { PrepareActions, WorkflowActions, UploadActions } from '@app/client/uploader/store/actions';
import { ResolveCustomizationService } from '@app/core/services/resolvers/resolve-customization';
import { ResolveExternalPromotionService } from '@app/core/services/resolvers/resolve-external-promotion';
import { Gnitekram } from '@smash-sdk/gnitekram';
import moment from 'moment';
import { cleanFilename } from '@app/shared/helpers/transferFile';

@Injectable()
export class UploadEffects {
  smashUploader: SmashUploader;
  logger: LoggerService;
  ngZone: NgZone;
  listeners: CustomEventEmitter[] = [];


  constructor(
    private actions$: Actions,
    private store$: Store<fromRoot.State & fromUploader.State>,
    private rawFileStoreService: RawFileStoreService,
    private gtmService: GoogleTagManagerService,
    private readonly localStorageService: LocalStorageService,
    private resolveCustomizationService: ResolveCustomizationService,
    private resolveExternalPromotionService: ResolveExternalPromotionService,
    private injector: Injector,
  ) {
    this.logger = this.injector.get(LoggerService);
    this.ngZone = this.injector.get(NgZone);
  }

  upload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadActions.Upload),
      concatLatestFrom(() => [
        this.store$.select(fromRoot.getCurrentDomain),
        this.store$.select(fromRoot.getSelectedLanguage),
        this.store$.select(fromRoot.getCurrentRegion),
        this.store$.select(fromRoot.getToken),
        this.store$.select(fromUploader.getUploadFormMode),
        this.store$.select(fromUploader.getTransferCommonParameters),
        this.store$.select(fromUploader.getTransferLinkParameters),
        this.store$.select(fromUploader.getTransferEmailParameters),
        this.store$.select(fromUploader.getDropboxPreview),
      ]),
      concatMap(([_, domain, language, region, token, uploadFormMode, commonParameters, linkParameters, emailParameters, dropbox]) => {
        const files = this.rawFileStoreService.getFiles().map(file => ({ name: cleanFilename(file.relativePath), file: file.file }));

        return forkJoin([this.resolveExternalPromotion(commonParameters), this.resolveCustomization(commonParameters)]).pipe(
          concatMap(([resolvedPromotion, resolvedCustomization]) => {
            commonParameters = {
              ...commonParameters,
              promotion: resolvedPromotion,
              customization: resolvedCustomization,
            };

            const uploadInput = buildUploadInput({ domain, language, commonParameters, linkParameters, emailParameters, uploadFormMode, dropbox, files });

            // Cast: { region, token } is the only subset of UploaderParameters this app supplies;
            // the other fields are optional. region/token can be absent around signout / expired-
            // session races, and the SmashUploader constructor now validates them and throws
            // InvalidParameterError SYNCHRONOUSLY (uploader >= 3.0.0-alpha.16 fail-fast) — which is
            // exactly why construction is deferred below so that throw is caught, not the cast masking
            // a real value the runtime constructor still enforces.
            const uploadParameters: UploaderParameters = { region, token } as UploaderParameters;
            this.saveDeliveryParameters(uploadInput.delivery as EmailDelivery | LinkDelivery);

            // Construct the uploader INSIDE defer() so the constructor's synchronous
            // InvalidParameterError throw (missing region/token) becomes an error notification routed
            // to the catchError below (SetUploadStatus(Error) + UploadFailure + fatal modal). If it
            // were constructed here in the concatMap projection, the throw would escape this pipe's
            // catchError entirely and leave the upload stuck at 'Pending' with no error surfaced.
            return defer(() => {
              this.smashUploader = new SmashUploader(uploadParameters);
              this.setupUploaderListeners();
              return this.smashUploader.upload(uploadInput);
            }).pipe(
              concatMap((output: UploadCompleteOutput | UploadCanceledOutput) => {
                if (output.status !== 'Canceling') {
                  return of(UploadActions.UploadSuccess({ transfer: output.transfer as UploadCompleteOutput['transfer'] }));
                }
                return of({ type: 'NO_ACTION' });
              }),
              catchError((error) => {
                const cloudfrontError = this.handleUploadError(error);
                // SetUploadStatus here guarantees the template transitions to the error
                // state even when the statusListener's ngZone.run() path has not yet fired.
                // Both paths are idempotent (reducer sets the same value), so double-dispatch
                // is harmless; we need at least one reliable in-zone dispatch.
                return of(
                  UploadActions.SetUploadStatus({ status: UploaderStatus.Error }),
                  UploadActions.UploadFailure({ error: cloudfrontError }),
                );
              }),
            );
          }),
        )
      }))
  );

  updateAndResumeUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadActions.UpdateAndResumeUpload),
      concatLatestFrom(() => [
        this.store$.select(fromRoot.getCurrentDomain),
        this.store$.select(fromRoot.getSelectedLanguage),
        this.store$.select(fromUploader.getUploadFormMode),
        this.store$.select(fromUploader.getTransferCommonParameters),
        this.store$.select(fromUploader.getTransferLinkParameters),
        this.store$.select(fromUploader.getTransferEmailParameters),
        this.store$.select(fromUploader.getDropboxPreview),
      ]),
      concatMap(([_, domain, language, uploadFormMode, commonParameters, linkParameters, emailParameters, dropbox]) => {
        if (!this.smashUploader) {
          // No live uploader to resume — it was reset/cancelled (or a late status emission raced
          // ahead of the reset), so there is nothing to "resume". Fall back to a fresh upload
          // instead of throwing an uncaught error that bounces the user off the flow. The Upload
          // effect carries the region/token context needed to construct a new SmashUploader.
          return of(UploadActions.Upload());
        }
        const files = this.rawFileStoreService.getFiles().map(file => ({ name: cleanFilename(file.relativePath), file: file.file }));

        return forkJoin([this.resolveExternalPromotion(commonParameters), this.resolveCustomization(commonParameters)]).pipe(
          concatMap(([resolvedPromotion, resolvedCustomization]) => {
            commonParameters = {
              ...commonParameters,
              promotion: resolvedPromotion,
              customization: resolvedCustomization,
            };

            const uploadInput = buildUploadInput({ domain, language, commonParameters, linkParameters, emailParameters, uploadFormMode, dropbox, files });
            this.saveDeliveryParameters(uploadInput.delivery as EmailDelivery | LinkDelivery);

            return defer(() => this.smashUploader.update(uploadInput)).pipe(
              concatMap(() => {
                return defer(() => this.smashUploader.resume()).pipe(
                  concatMap(({ transfer }) => {
                    if (transfer?.queue > 0) {
                      // Sync the store's queue/queuedUntil to THIS resume() response before
                      // reopening the modal -- without this, the modal's countdown reads
                      // whatever queue value was already in the store from before the resume
                      // attempt (stale), rather than the fresh one resume() just returned.
                      this.store$.dispatch(UploadActions.UploadQueued({ queue: transfer.queue, queuedUntil: transfer.queuedUntil }));
                      this.store$.dispatch(UploadActions.SetUploadStatus({ status: UploaderStatus.Queued }));
                      this.store$.dispatch(PrepareActions.DisplayModalSizePremium());
                    }
                    return of(UploadActions.UpdateAndResumeUploadSuccess());
                  }),
                );
              }),
              catchError((error) => {
                const cloudfrontError = this.handleUploadError(error);
                return of(UploadActions.UpdateAndResumeUploadFailure({ error: cloudfrontError }));
              }),
            );
          }));
      })
    )
  );

  cancelUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadActions.CancelUpload),
      map(() => {
        this.store$.dispatch(WorkflowActions.updateStep({ step: 'PREPARE' }));
        this.listeners.map(listener => listener.off());
        if (this.smashUploader) {
          this.smashUploader.cancel();
          this.smashUploader.off();
          this.smashUploader = null;
        }
        return UploadActions.CancelUploadSuccess();
      })
    )
  );

  pauseUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadActions.PauseUpload),
      concatMap(() => {
        if (!this.smashUploader) {
          return of({ type: 'NO_ACTION' });
        }
        return defer(() => this.smashUploader.pause()).pipe(
          map(() => ({ type: 'NO_ACTION' })),
          catchError((error) => {
            // alpha.5 rejects pause() when the transfer is not in a pausable state (already
            // Paused, Finishing, Canceled, Error, ...). The StatusEvent listener remains the
            // single source of truth for the UI (setupUploaderListeners), so we only log and
            // swallow here — we must not dispatch a status ourselves.
            this.logger.error(error);
            return of({ type: 'NO_ACTION' });
          }),
        );
      })
    )
  );

  resumeUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadActions.ResumeUpload),
      concatMap(() => {
        if (!this.smashUploader) {
          return of({ type: 'NO_ACTION' });
        }
        return defer(() => this.smashUploader.resume()).pipe(
          map(() => ({ type: 'NO_ACTION' })),
          catchError((error) => {
            // Same rationale as pauseUpload$: alpha.5 rejects resume() on an invalid transfer
            // state, so we log and swallow rather than dispatch a status — the StatusEvent
            // listener owns the transition back to Started.
            this.logger.error(error);
            return of({ type: 'NO_ACTION' });
          }),
        );
      })
    )
  );

  resetUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadActions.ResetUpload),
      map(() => {
        this.store$.dispatch(WorkflowActions.updateStep({ step: 'LANDING' }));
        this.listeners.map(listener => listener.off());
        if (this.smashUploader) {
          this.smashUploader.cancel();
          this.smashUploader.off();
          this.smashUploader = null;
        }
        this.rawFileStoreService.reset();
        return UploadActions.ResetUploadSuccess();
      })
    )
  );

  checkFirstTimeSender$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadActions.CheckFirstTimeSender),
      concatLatestFrom(() => [
        this.store$.select(fromRoot.getActiveIdentity),
      ]),
      concatMap(([action, identity]) => {
        if (!identity?.username) {
          const marketingSdk = new Gnitekram();
          const params = { id: unescape(encodeURIComponent(action.username)), key: action.transferId };
          return defer(() => marketingSdk.getUsage(params)).pipe(
            map(({ usage }) => {
              const isWithinLast14Days = usage?.firstSendDate && moment(usage.firstSendDate).isAfter(moment().subtract(14, 'days'));
              if (usage?.firstSendDate && isWithinLast14Days) {
                this.gtmService.pushTag({ event: Constant.eventGTM.FIRST_TIME_SENDER });
              }
              return UploadActions.CheckFirstTimeSenderSuccess({ usage });
            }),
            catchError((error) => {
              // Dispatch a PLAIN payload, never the raw SDK Error instance: with the NgRx dev-mode
              // runtime checks active (strictActionSerializability), an Error inside an action
              // throws "Detected unserializable action at 'error'" INSIDE the store pipeline the
              // moment this failure action lands (e.g. the marketing endpoint rejecting after an
              // upload finishes) — after which subsequent dispatches stop updating the state while
              // actions keep reaching the effects, silently breaking everything that follows
              // (observed: the post-"new smash" re-upload reading stale pristine parameters).
              // No reducer consumes this payload; it exists for traceability only.
              return of(UploadActions.CheckFirstTimeSenderFailure({ error: { name: error?.name, message: error?.message, code: error?.code } }));
            })
          );
        }
        return of({ type: 'NO_ACTION' });
      })
    )
  );

  private resolveExternalPromotion(commonParameters: TransferCommonParameters) {
    return this.resolveExternalPromotionService.resolve().pipe(
      take(1),
      map(({ selectedPromotion, promotions }) => {
        return commonParameters.promotion || { id: selectedPromotion?.id || promotions.find(p => p.id)?.id };
      })
    );
  }

  private resolveCustomization(commonParameters: TransferCommonParameters) {
    return this.resolveCustomizationService.resolve().pipe(
      take(1),
      map(({ customization }) => {
        // Optional-chain BOTH levels. On a no-branding domain the reducer stores a truthy husk
        // (`{ logo: undefined, background: undefined }`, from loadCustomizationSuccess with a null
        // customization), so guarding only `customization` throws on `.sourceUrl` -- inside forkJoin,
        // outside the catchError below, which kills the effect and makes the Send button do nothing.
        return commonParameters.customization || { background: customization?.background?.sourceUrl, logo: customization?.logo?.sourceUrl };
      })
    );
  }

  private setupUploaderListeners() {
    // Single source of truth for the upload status: the SDK emits a `status` event (StatusEvent)
    // on EVERY transition — Starting, Started, Queued, Resumable, Finishing, Finished, and the
    // terminal/paused states NetworkPaused, Error, Canceled — carrying the authoritative
    // UploaderStatus. We reflect it verbatim and let the SDK own the state machine; the app no
    // longer derives the status from discrete or network events. That derivation was a leftover
    // of the pre-SDK connectivity workaround and produced wrong transitions — e.g. forcing
    // `Started` on a connectivity resume (the SDK deliberately emits NO `started` on resume),
    // which could revert a `Finishing` upload or resurrect a `Canceled` one.
    //
    // ngZone.run: SDK events fire outside Angular's zone. Wrapping the dispatch keeps change
    // detection correct AND guarantees the terminal transition (Error/Canceled) surfaces in-zone
    // even if the `upload()` promise rejection stalls or never arrives — the load-bearing property
    // of the former error/canceled listeners, now covered here for every status.
    //
    // The SDK's `UploaderEvents` enum omits the "status" name; we subscribe by StatusEvent's own
    // event name (`on` accepts a plain string), typed via the exported StatusEvent class.
    const statusListener = this.smashUploader.on<StatusEvent>('status', (event) => {
      this.ngZone.run(() => {
        this.store$.dispatch(UploadActions.SetUploadStatus({ status: event.data.status }));
      });
    });

    // Discrete lifecycle listeners below are kept ONLY for their non-status side-effects
    // (analytics tags, queue metadata, progress figures) — never to set the status.
    const startedListener = this.smashUploader.on<StartedEvent>(UploaderEvents.Started, (data) => {
      this.gtmService.pushTag({ event: Constant.eventGTM.TRANSFER_INITIATED, transfer_id: data.data.transfer.id, });
    });

    const queuedListener = this.smashUploader.on<QueuedEvent>(UploaderEvents.Queued, (event) => {
      this.store$.dispatch(UploadActions.UploadQueued({ queue: event.data.transfer.queue, queuedUntil: event.data.transfer.queuedUntil }));
      this.store$.dispatch(PrepareActions.DisplayModalSizePremium());
    });

    const finishedListener = this.smashUploader.on<FinishedEvent>(UploaderEvents.Finished, (data) => {
      this.gtmService.pushTag({ event: Constant.eventGTM.TRANSFER_LOCKED, transfer_id: data.data.transfer.id, });
      this.store$.dispatch(UploadActions.CheckFirstTimeSender({ username: data.data.transfer.delivery!.sender!.email, transferId: data.data.transfer.id }));
    });

    const progressListener = this.smashUploader.on<UploadProgressEvent>(UploaderEvents.Progress, (event) => {
      this.store$.dispatch(UploadActions.SetProgress({ progress: event.data.percent }));
      this.store$.dispatch(UploadActions.SetUploadedBytes({ uploadedBytes: event.data.uploadedBytes }));
      this.store$.dispatch(UploadActions.SetRemainingSize({ remainingSize: event.data.totalBytes - event.data.uploadedBytes }));
      if (event.data.speed > 0) {
        this.store$.dispatch(UploadActions.SetUploadSpeed({ speed: event.data.speed }));
      }
    });

    this.listeners = [statusListener, startedListener, queuedListener, finishedListener, progressListener];
  }

  private handleUploadError(error: UploaderError) {
    const parsedError = parseUploaderError(error);
    const cloudfrontError = buildCloudfrontError<UploaderError>(parsedError);
    this.logger.error(cloudfrontError);
    return cloudfrontError;
  }

  private saveDeliveryParameters(deliveryParameters: EmailDelivery | LinkDelivery) {
    this.localStorageService.setItem('lastTransferParameters', btoa(unescape(encodeURIComponent(JSON.stringify(deliveryParameters)))));
  }

}
